import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ArrowLeft, Wallet, Users, CalendarDays, MapPin, Clock } from "lucide-react";
import api from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function fmtDate(d) {
  try { return format(new Date(d), "EEE, dd MMM"); } catch { return d; }
}

export default function TeamDetail() {
  const { teamName } = useParams();
  const team = decodeURIComponent(teamName);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/auction/teams"), api.get("/auction/players"), api.get("/fixtures")])
      .then(([t, p, f]) => { setTeams(t.data); setPlayers(p.data); setFixtures(f.data); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const row = teams.find((t) => t.team === team);
  const roster = players.filter((p) => p.status === "sold" && p.sold_to === team);
  const teamFixtures = fixtures.filter((f) => f.team_a === team || f.team_b === team);

  if (loaded && !row) {
    return (
      <main className="pt-40 pb-24 bg-league-ink min-h-screen text-white text-center" data-testid="team-not-found">
        <h1 className="font-display text-4xl">FRANCHISE NOT FOUND</h1>
        <Link to="/teams" className="inline-block mt-6 text-league-red font-bold uppercase tracking-wider text-sm" data-testid="team-back-link">← All teams</Link>
      </main>
    );
  }

  return (
    <main className="bg-league-ink min-h-screen text-white" data-testid="team-detail-page">
      <section className="relative overflow-hidden pt-36 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-league-deep via-league-ink to-league-ink" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <Link to="/teams" className="inline-flex items-center gap-2 text-white/50 text-sm font-semibold uppercase tracking-wider transition-colors hover:text-white mb-8" data-testid="team-back">
            <ArrowLeft size={15} /> All teams
          </Link>
          <div className="flex flex-wrap items-center gap-6">
            <motion.img
              src="/asl-logo.png"
              alt=""
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="h-24 sm:h-32 w-auto drop-shadow-2xl"
            />
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
                className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter"
                data-testid="team-name"
              >
                {team}
              </motion.h1>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2, ease: EASE }}
                className="mt-4 flex flex-wrap gap-3"
              >
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-bold uppercase tracking-widest" data-testid="team-squad-count">
                  <Users size={13} className="text-league-red" /> {roster.length} in squad
                </span>
                {row?.purse_total > 0 && (
                  <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-bold uppercase tracking-widest" data-testid="team-purse-left">
                    <Wallet size={13} className="text-league-red" /> {inr(row.purse_left)} left of {inr(row.purse_total)}
                  </span>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20" data-testid="team-roster-section">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">SQUAD <span className="text-outline-white">6.0</span></h2>
        {roster.length === 0 && (
          <div className="border-2 border-dashed border-white/15 rounded-2xl p-12 text-center" data-testid="team-roster-empty">
            <p className="font-oswald text-xl font-semibold">No players bought yet</p>
            <p className="text-white/50 text-sm mt-2">The squad fills up on auction night — follow it live on the Auction page.</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {roster.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.06, ease: EASE }}
              className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-center gap-4 hover:bg-white/10 transition-colors duration-200"
              data-testid={`roster-player-${p.id}`}
            >
              <img src={p.image_url || "/asl-logo.png"} alt={p.name} className={`w-14 h-14 rounded-full object-cover border-2 border-white/15 shrink-0 ${p.image_url ? "" : "bg-white p-1"}`} />
              <div className="min-w-0 flex-1">
                <p className="font-oswald text-lg font-semibold truncate">{p.name}</p>
                <p className="text-xs text-white/50 uppercase tracking-widest">{p.role}</p>
              </div>
              <span className="font-oswald text-xl font-semibold text-league-red shrink-0">{inr(p.sold_price)}</span>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24" data-testid="team-fixtures-section">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">SEASON 6 <span className="text-outline-white">FIXTURES</span></h2>
        {teamFixtures.length === 0 && (
          <div className="border-2 border-dashed border-white/15 rounded-2xl p-12 text-center" data-testid="team-fixtures-empty">
            <p className="font-oswald text-xl font-semibold">Fixtures dropping soon</p>
            <p className="text-white/50 text-sm mt-2">The February 2027 schedule is being finalised.</p>
          </div>
        )}
        <div className="space-y-4">
          {teamFixtures.map((f, i) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.45, delay: i * 0.05, ease: EASE }}
              className="bg-white/5 border border-white/10 rounded-xl p-5 flex flex-wrap items-center gap-4 justify-between"
              data-testid={`team-fixture-${f.id}`}
            >
              <div>
                <p className="font-oswald text-lg font-semibold">
                  {f.team_a} <span className="text-league-red">vs</span> {f.team_b}
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50 mt-1">
                  <span className="flex items-center gap-1.5"><CalendarDays size={12} /> {fmtDate(f.date)}</span>
                  {f.time && <span className="flex items-center gap-1.5"><Clock size={12} /> {f.time}</span>}
                  {f.venue && <span className="flex items-center gap-1.5"><MapPin size={12} /> {f.venue}</span>}
                </div>
              </div>
              {f.status === "completed" && f.result ? (
                <span className="text-sm font-semibold text-white/70">{f.result}</span>
              ) : f.status === "live" ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-league-red text-[10px] font-bold uppercase tracking-widest">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full bg-league-blue text-[10px] font-bold uppercase tracking-widest">Upcoming</span>
              )}
            </motion.div>
          ))}
        </div>
      </section>
    </main>
  );
}
